import { useEffect, useRef, useState } from 'react';

import type { UseComboboxStateChange } from 'downshift';
import { useCombobox } from 'downshift';
import type { ChangeEvent, Dispatch, ReactNode, SetStateAction } from 'react';
import { styled } from 'styled-components';
import useOnClickOutside from 'utils/hooks/useClickOutside';
import { getItemBorderInvertedColor } from 'views/Widget/Widget.helper';

import { ReactComponent as IconClear } from './clear.svg';
import { ReactComponent as IconArrow } from './icon-arrow-up.svg';
import { ReactComponent as IconSelectSpin } from './icon-select-spin.svg';
import { ReactComponent as IconLoader } from './loader.svg';
import { ReactComponent as IconSearch } from './search.svg';

interface ComboboxProps<T> {
  inputValueChange: string;
  isFetching: boolean;
  items: T[];
  onInputValueChange: Dispatch<SetStateAction<string>>;
  renderItem: (item: T | undefined) => ReactNode;
  value: T | undefined;
  borderColor?: string;
  className?: string;
  defaultItem?: T;
  disabled?: boolean;
  isInitialFetching?: boolean;
  isShowFetchingInToggle?: boolean;
  isWidget?: boolean;
  itemToString?: (item: T | null) => string;
  onChange?: (changes: UseComboboxStateChange<T>) => void;
  openToTop?: boolean;
  placeholder?: string;
  textColor?: string;
}

const Arrow = styled(IconArrow)<{ $color?: string }>`
  margin-right: 16px;
  margin-left: 16px;
  width: 24px;
  height: 24px;
  color: ${({ $color }) => $color ?? 'var(--contentPrimary)'};
  flex-grow: 0;
  flex-shrink: 0;
  transition: transform 0.3s ease-in-out;
  &[data-visible='true'] {
    transform: rotate(180deg);
  }
`;

const Search = styled(IconSearch)`
  flex-shrink: 0;
`;

const RenderedItemContainer = styled.div<{ $textColor?: string }>`
  flex-grow: 1;
  span {
    color: ${({ $textColor }) => $textColor ?? 'unset'};
  }
  overflow: hidden;
`;

const ToggleButton = styled.div<{ $borderColor?: string; $textColor?: string }>`
  text-align: start;
  height: 60px;
  display: flex;
  align-items: center;
  border-radius: 12px;
  border: ${({ $borderColor }) => ($borderColor ? `1px solid ${$borderColor}` : '1px solid var(--borderPrimarySolid)')};
  color: ${({ $textColor }) => $textColor ?? 'var(--primary)'};
  transition: 0.3s ease-in-out;

  &[aria-expanded='true'] {
    border-radius: 12px 12px 0 0;
  }

  &[data-is_selector='true'] {
    cursor: pointer;
  }
`;

const Item = styled.li`
  height: 48px;
  display: flex;
  align-items: center;
  transition: 0.3s ease-in-out;

  &[data-current='true'] {
    background-color: var(--backgroundAlpha_2);
  }

  &:hover {
    cursor: pointer;
  }
`;

const Loader = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(359deg);
    }
  }

  svg {
    animation: spin 0.5s linear infinite;
  }
`;

const LoaderInToggle = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(359deg);
    }
  }

  svg {
    animation: spin 0.5s linear infinite;

    margin-right: 16px;
    margin-left: 16px;

    path {
      color: var(--primary) !important;
    }
  }
`;

const ItemsContainer = styled.div`
  position: relative;
  width: 100%;
`;

const Input = styled.input<{ $textColor?: string }>`
  color: ${({ $textColor }) => $textColor ?? 'var(--primary)'};
  border: none;
  background-color: transparent;
  width: 50px;
  flex-grow: 1;
`;

const ResetButton = styled.button`
  border: none;
  background-color: transparent;
  flex-shrink: 0;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  opacity: 0;
  transform: translateX(30px);
  padding-top: 5px;

  & svg {
    color: var(--primary);
    alight-items: center;
    path {
      transition: fill 0.3s ease-in-out;
    }
  }

  &[data-isvisible='true'] {
    opacity: 1;
    transform: translateX(0);
  }

  &:hover {
    svg path {
      fill: var(--bsPrimaryHoverBackground);
    }
  }
  &:active {
    svg path {
      fill: var(--buttonPrimaryBackground);
    }
  }
`;

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  gap: 8px;
  height: 48px;
  border-bottom: 1px solid var(--borderPrimarySolid);
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
`;

const Menu = styled.ul<{ $borderColor?: string; $isWidget?: string }>`
  overflow-x: hidden;
  overflow-y: auto;
  width: 100%;
  list-style: none;
  position: absolute;
  border-radius: 0 0 12px 12px;
  height: auto;
  max-height: 0;
  transition: max-height 0.3s ease-in-out;
  background-color: ${({ $isWidget }) => ($isWidget ? '#FFFFFF' : 'var(--backgroundApp)')};
  z-index: 10;
  border-left: ${({ $borderColor }) =>
    $borderColor ? `1px solid ${$borderColor}` : '1px solid var(--borderPrimarySolid)'};
  border-right: ${({ $borderColor }) =>
    $borderColor ? `1px solid ${$borderColor}` : '1px solid var(--borderPrimarySolid)'};
  &[data-visible='true'] {
    max-height: 250px;
    border-bottom: ${({ $borderColor }) =>
      $borderColor ? `1px solid ${$borderColor}` : '1px solid var(--borderPrimarySolid)'};
    border-top: none;

    ${InputContainer} {
      opacity: 1;
      border-bottom: ${({ $borderColor }) =>
        $borderColor ? `1px solid ${$borderColor}` : '1px solid var(--borderPrimarySolid)'};
    }
  }

  &[data-open_to_top='true'] {
    bottom: 100%;

    &[data-visible='true'] {
      border-top: ${({ $borderColor }) =>
        $borderColor ? `1px solid ${$borderColor}` : '1px solid var(--borderPrimarySolid)'};
      border-radius: 12px 12px 0 0;
    }
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: center;
  align-self: center;
  position: relative;

  &[data-disabled='true'] {
    opacity: 0.5;
    pointer-events: none;
  }

  &[data-fetching='true'] {
    pointer-events: none;
  }
`;

const DropdownCombobox = <T,>({
  placeholder,
  value,
  items,
  onChange,
  renderItem,
  defaultItem,
  itemToString,
  isInitialFetching,
  isFetching,
  inputValueChange,
  onInputValueChange,
  disabled,
  openToTop,
  className,
  borderColor,
  textColor,
  isWidget,
  isShowFetchingInToggle,
}: ComboboxProps<T>) => {
  const { getMenuProps, getInputProps, highlightedIndex, getItemProps, selectedItem } = useCombobox({
    itemToString,
    items,
    onSelectedItemChange: onChange,
    scrollIntoView: () => null,
    selectedItem: value,
    stateReducer: (state, actionAndChanges) => {
      const { type, changes } = actionAndChanges;

      if (type === useCombobox.stateChangeTypes.FunctionSetInputValue && changes.inputValue === '') {
        return {
          ...changes,
          isOpen: true,
        };
      }

      return changes;
    },
  });

  const ref = useRef(null);

  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

  useOnClickOutside(ref, () => setIsMenuOpen(false));

  const currentItem = selectedItem ?? defaultItem;

  const isSelector = (): boolean => {
    if (items?.length > 1) return true;

    if (!(itemToString && itemToString(selectedItem))) return true;

    if (inputValueChange) return true;

    if (!inputValueChange && (isInitialFetching ?? isFetching)) return true;

    return false;
  };

  useEffect(() => {
    onInputValueChange('');
  }, [isMenuOpen]);

  const inputElement = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputElement.current && isMenuOpen) {
      inputElement.current.focus({ preventScroll: true });
    }
  }, [isMenuOpen]);

  return (
    <Container ref={ref} data-disabled={disabled} data-fetching={isShowFetchingInToggle}>
      <ToggleButton
        $borderColor={borderColor}
        $textColor={textColor}
        aria-expanded={isMenuOpen}
        className={className}
        data-is_selector={isSelector()}
        onClick={() => {
          if (isSelector()) setIsMenuOpen(!isMenuOpen);
        }}
      >
        <RenderedItemContainer $textColor={textColor}>{renderItem(currentItem)}</RenderedItemContainer>
        {isShowFetchingInToggle ? (
          <Item>
            <LoaderInToggle>
              <IconSelectSpin />
            </LoaderInToggle>
          </Item>
        ) : (
          isSelector() && <Arrow $color={textColor} data-visible={isMenuOpen} />
        )}
      </ToggleButton>
      <ItemsContainer>
        <Menu
          {...getMenuProps()}
          $borderColor={getItemBorderInvertedColor(borderColor)}
          $isWidget={isWidget}
          data-open_to_top={openToTop}
          data-visible={isMenuOpen}
        >
          <InputContainer>
            <Search />
            <Input
              {...getInputProps({
                onChange: (e: ChangeEvent<HTMLInputElement>) => {
                  onInputValueChange(e.target.value);
                },
                placeholder,
                ref: inputElement,
                value: inputValueChange,
              })}
              $borderColor={borderColor}
              $textColor='var(--primary)'
            />
            <ResetButton
              data-isvisible={!!inputValueChange.length}
              type='button'
              onClick={() => onInputValueChange('')}
            >
              <IconClear />
            </ResetButton>
          </InputContainer>
          {isFetching && (
            <Item>
              <Loader>
                <IconLoader />
              </Loader>
            </Item>
          )}
          {isFetching
            ? null
            : items?.map((item, index) => (
                <Item
                  key={item}
                  data-current={highlightedIndex === index}
                  {...getItemProps({
                    index,
                    item,
                    onClick: () => setIsMenuOpen(false),
                  })}
                >
                  {renderItem(item)}
                </Item>
              ))}
        </Menu>
      </ItemsContainer>
    </Container>
  );
};

export default DropdownCombobox;
